import React, { useEffect, useState, useRef } from "react";
import { MONTHS, WEEKDAYS_SHORT } from '../../../constants';
import { tConv24, everyNminutes, rearrangeDataForWeekView, calendarWidgetVisitTypeBasedColor } from '../../../calendarFunctions'; 
import { isSet } from "../../../../../utils/commonUtils"; 
import Wrap from "../../../../../hoc/Wrap";
import { getCurrentTime } from "../../../../../utils/commonUtils";

// ✅ Convert "HH:MM AM/PM" → total minutes since midnight
const timeToMinutes = (timeStr) => {
  const [time, modifier] = timeStr.split(" ");
  let [hours, minutes] = time.split(":").map(Number);
  if (modifier === "PM" && hours !== 12) hours += 12;
  if (modifier === "AM" && hours === 12) hours = 0;
  return hours * 60 + minutes;
};

const DayViewBody = (props) => {
  const {
    timeInterval,
    showMoreDetailsDataHandler,
    addVisitToogleHandler,
    calendarRecords,
    currentDate,
    currentDayDetails,
    blockedDates = [],
    blockedDays = [],
    availabilitySlots = {},
    showQuickVisitView,
  } = props;

  const weekdayNames = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
  const calendarWeeksRecords = rearrangeDataForWeekView(calendarRecords);
  const [maxSlotDisplay, setMaxSlotDisplay] = useState(3);
  
  // New state and ref for the blue line
  const [currentTimePosition, setCurrentTimePosition] = useState(null);
  const tableRef = useRef(null);
  
  useEffect(() => {
    const updateMaxSlotDisplay = () => {
      const calendarDiv = document.getElementById('calendar');
      if (calendarDiv) {
        const calendarRect = calendarDiv.getBoundingClientRect();
        const { width } = calendarRect;
        setMaxSlotDisplay(Math.floor(width / 180));
      }
    };
    
    updateMaxSlotDisplay();
    window.addEventListener('resize', updateMaxSlotDisplay);
    
    return () => {
      window.removeEventListener('resize', updateMaxSlotDisplay);
    };
  }, [props]);
  
  // ✅ New useEffect hook to position the blue line
  useEffect(() => {
    const updateBlueLinePosition = () => {
      const orgTime = getCurrentTime();
      const slots = tableRef.current?.querySelectorAll("tr");
      if (!slots || slots.length === 0) return;

      const orgTimeMinutes = timeToMinutes(orgTime);

      for (let i = 0; i < slots.length; i++) {
        const currentSlot = slots[i];
        const timeStr = currentSlot.getAttribute("data-time");
        if (timeStr && timeToMinutes(timeStr) === orgTimeMinutes) {
          const mainSlotOffset = currentSlot.offsetTop;
          const nextSlot = slots[i + 1]; // This is the 'fc-minor' row
          
          if (nextSlot) {
            const nextSlotOffset = nextSlot.offsetTop;
            const midpoint = (mainSlotOffset + nextSlotOffset) / 2;
            setCurrentTimePosition(midpoint);
            return;
          }
        }
      }
    };

    updateBlueLinePosition();
    window.addEventListener('resize', updateBlueLinePosition);
    const intervalId = setInterval(updateBlueLinePosition, 60000);

    return () => {
      window.removeEventListener('resize', updateBlueLinePosition);
      clearInterval(intervalId);
    };
  }, [timeInterval, currentDate ,props]); // Added currentDate to dependencies

  return (
    <tbody className="fc-body">
      <tr>
        <td className="fc-widget-content">
          <div className="fc-scroller fc-time-grid-container">
            <div className="fc-time-grid">
              {currentTimePosition !== null && (
                <>
                  <div
                    style={{
                      position: "absolute",
                      top: `${currentTimePosition}px`,
                      left: "0",
                      right: "0",
                      height: "3px",
                      backgroundColor: "#174ADD",
                      zIndex: 10
                    }}
                  ></div>
                  <div
                    style={{
                      position: "absolute",
                      top: `${currentTimePosition - 4}px`,
                      left: "0",
                      width: "10px",
                      height: "10px",
                      backgroundColor: "#174ADD",
                      borderRadius: "50%",
                      zIndex: 15
                    }}
                  ></div>
                </>
              )}
              
              <div className="fc-bg">
                <table className="">
                  <tbody>
                    <tr>
                      <td className="fc-axis fc-widget-content" style={{width:'46.7188px'}}></td>
                      <td className="fc-day fc-widget-content fc-sun fc-past" data-date={props.currentDate}></td>
                    </tr>
                  </tbody>
                </table>
              </div>  
              <div className="fc-slats">
                <table ref={tableRef}>
                  <tbody>
                    {timeInterval.map((time, timeIndex) => {
                      const timeIn12 = tConv24(time);
                      const timeMinutes = timeToMinutes(timeIn12);
                      const firstDay = currentDayDetails[0];
                      const weekdayName = weekdayNames[new Date(firstDay.date).getDay()];
                      const holiday = blockedDates.find(b => b.date === firstDay.c_date);
                      const isDayBlocked = blockedDays.includes(weekdayName);
                      const slots = availabilitySlots[weekdayName] || [];
                      let isTimeAllowed = true;

                      if (slots.length > 0) {
                        isTimeAllowed = slots.some(slot =>
                          timeMinutes >= timeToMinutes(slot.from_time) &&
                          timeMinutes <= timeToMinutes(slot.to_time)
                        );
                      }
                      const isBlocked =
                        !!holiday || isDayBlocked || (slots.length > 0 && !isTimeAllowed);
                      const tooltip = holiday
                        ? holiday.reason
                        : isDayBlocked
                        ? "Clinic Not Available"
                        : (slots.length > 0 && !isTimeAllowed)
                        ? "Outside Available Hours"
                        : "";
                      return (
                        <Wrap key={timeIndex}>
                          <tr data-time={time}>
                            <td className="fc-axis fc-time fc-widget-content" style={{ width: "46.7px" }}>
                              <span>{timeIn12}</span>
                            </td>
                            <td
                              className={`fc-widget-content ${isBlocked ? "fc-day-blocked sh-dayView" : "sh-dayView"}`}
                              style={isBlocked ? { cursor: "not-allowed" } : {}}
                              title={tooltip}
                              onClick={() => {
                                if (!isBlocked) {
                                  addVisitToogleHandler({
                                    visit_date: firstDay.c_date,
                                    visit_time: timeIn12,
                                  });
                                }
                              }}
                            >
                              {currentDayDetails.map((dateItem, ind) => {
                                const itemData = isSet(
                                  calendarWeeksRecords?.[dateItem.c_date]?.[timeIn12],
                                  []
                                );
                                return itemData.map((item, idx) => {
                                  if (idx + 1 > maxSlotDisplay) return false;
                                  const typeBasedClass = calendarWidgetVisitTypeBasedColor(item.visit_category, 'GRID', item.visit_status);
                                  return (
                                    <React.Fragment key={`${ind}-${idx}`}>
                                      <a
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          showQuickVisitView(e, item);
                                        }}
                                      >
                                        <div
                                          className={`mainValueTag ${typeBasedClass}`}
                                          style={{ width: "160px" }}
                                          title={`${item.visit_time} : ${item.employee.first_name} ${item.employee.last_name}`}
                                        >
                                          <span className="text-sm font-weight-bold">{item.visit_time}</span>
                                          <span className="font-weight-bold"> : </span>
                                          <span className="text-sm">
                                            {item.employee.first_name} {item.employee.last_name}
                                          </span>
                                        </div>
                                      </a>
                                      {(idx + 1) === maxSlotDisplay && itemData.length > maxSlotDisplay && (
                                        <a
                                          className="text-danger textUnderline text-small quick-sidebar-toggler cursor-pointer"
                                          style={{ display: "block", textAlign: "right" }}
                                          onClick={(e) =>
                                            showMoreDetailsDataHandler(
                                              e,
                                              { date: dateItem.c_date, time: timeIn12 },
                                              isSet(itemData, [])
                                            )
                                          }
                                        >
                                          + More ...
                                        </a>
                                      )}
                                    </React.Fragment>
                                  );
                                });
                              })}
                            </td>
                          </tr>
                          <tr className="fc-minor" id={`calendarRow_${time.toString().replace(":", "")}`}>
                            <td className="fc-axis fc-time fc-widget-content" style={{ width: "46.7px" }}></td>
                            <td
                              className={`fc-widget-content ${isBlocked ? "fc-day-blocked sh-dayView" : ""}`}
                              style={isBlocked ? { cursor: "not-allowed" } : {}}
                              title={tooltip}
                            />
                          </tr>
                        </Wrap>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </td>
      </tr>
    </tbody>
  );
};

export default DayViewBody;